import React from 'react';
import styled from 'styled-components';
import { TYPE } from 'theme';
import { formatHashValue } from 'utils';
import { Profile, Social } from './config';
import { Bonus } from 'components/styled/Box';
import { SocialAuthCode } from 'constant/socialAuth';

const StyledContent = styled.div`
    position: relative;
    max-width: 300px;
    padding-bottom: 20px;
`;
const StyledBonus = styled(Bonus)`
    font-size: 40px;
    text-align: center;
    padding: 20px 0;
`;

interface Props {
    code?: SocialAuthCode; // bind success/error code
    bonus?: number;
    profile?: Profile;
    social: Social;
    bindStats?: any;
}

const BindSuccess = ({ code, social, profile, bonus }: Props) => {
    return (
        <StyledContent>
            <TYPE.main fontSize={16} fontWeight={700} marginBottom={20}>
                Bind Social Account
            </TYPE.main>
            {bonus ? <StyledBonus>+{bonus} Social Points</StyledBonus> : null}
            <TYPE.desc fontSize={14}>
                You’ve bounded {social?.label} account (@{profile?.name}) to EVM address (
                {formatHashValue(profile?.bindAddress)}) successfully.
            </TYPE.desc>
        </StyledContent>
    );
};

const getErrorInfo = (code: SocialAuthCode, socialLabel: string, bindStates?: any) => {
    if (code === SocialAuthCode.Success) {
        return;
    }
    switch (code) {
        case SocialAuthCode.TwitterHasBeenUsed:
            return (
                <span>
                    {socialLabel} account {bindStates?.twitterName ? `(@${bindStates.twitterName})` : ''} has bounded
                    address <span style={{ wordBreak: 'break-all' }}>({bindStates?.address})</span>. Please remove the
                    original connection and try again later.
                </span>
            );
        case SocialAuthCode.UserNotExists:
            return `User is not exists. Please check your address and try again later.`;
        case SocialAuthCode.UserHasAssociatedWithTwitter:
            return `Current address has already been associated with other ${socialLabel} account. Please remove the original connection and try again later.`;
        case SocialAuthCode.TwitterCreatedTooShort:
            return `Oops! Your X account was created less than 90 days ago.`;
        case SocialAuthCode.TwitterError:
            return `The operation was rejected by ${socialLabel}.`;
        case SocialAuthCode.DiscordError:
            return `The operation was rejected by Discord.`;
        case SocialAuthCode.DiscordHasBeenUsed:
            return (
                <span>
                    Discord account (@{bindStates.discordName}) has bounded address{' '}
                    <span style={{ wordBreak: 'break-all' }}>({bindStates?.address})</span>. Please remove the original
                    connection and try again later.
                </span>
            );
        case SocialAuthCode.UserHasAssociatedWithDiscord:
            return `Current address has already been associated with another Discord account. Please remove the original connection and try again later.`;
        case SocialAuthCode.UnknownError:
        default:
            return 'Something went wrong. Please try again later.';
    }
};

const BindFail = ({ social, code, bindStats }: Props) => {
    return (
        <StyledContent>
            <TYPE.main fontSize={16} fontWeight={700} marginBottom={20}>
                Connect {social?.label} Error
            </TYPE.main>
            <TYPE.desc fontSize={14}>
                {getErrorInfo(code!, social.label, bindStats)}
                {/* {social?.label} account({profile?.username}) has bonded to address ({formatHashValue(profile?.bindAddress)}
                ). please remove the original connection and try again later. */}
            </TYPE.desc>
        </StyledContent>
    );
};

export const BindResult = ({ code, bindStats, social, profile, bonus }: Props) => {
    if (!code) {
        return;
    }
    if ([SocialAuthCode.Success].includes(code)) {
        return <BindSuccess code={code} bonus={bonus} social={social} profile={profile} />;
    }
    return <BindFail code={code} bindStats={bindStats} social={social} profile={profile} />;
};

export const RemoveConfirm = () => {
    return (
        <StyledContent>
            <TYPE.main fontSize={16} fontWeight={700} marginBottom={20}>
                Remove Social Account
            </TYPE.main>
            <TYPE.desc fontSize={14}>
                Removing the binding will prevent you from completing social tasks in the future.
            </TYPE.desc>
        </StyledContent>
    );
};

export const RemoveResult = ({ code, social, profile }: Props) => {
    return (
        <StyledContent>
            <TYPE.main fontSize={16} fontWeight={700} marginBottom={20}>
                Remove{code === SocialAuthCode.Success ? ' Successfully' : ' Failed'}
            </TYPE.main>
            <TYPE.desc fontSize={14}>
                You have {code === SocialAuthCode.Success ? 'successfully' : 'fail to'} removed the connection between{' '}
                {social.label} account (@{profile?.name}) and address ({formatHashValue(profile?.bindAddress)}).
            </TYPE.desc>
        </StyledContent>
    );
};
